/**
 * Preference Utilities
 *
 * Helper functions for preference display components.
 */

/**
 * Get a short label for spice level (0-5)
 */
export function getSpiceLevelLabel(level: number): string {
  const labels = ['No Spice', 'Mild', 'Medium', 'Warm', 'Hot', 'Extreme']
  return labels[Math.min(Math.max(level, 0), 5)]
}

/**
 * Format Scoville heat units for display
 * e.g., 100000 -> "100K", 1500000 -> "1.5M"
 */
export function formatScoville(shu: number): string {
  if (!shu || shu <= 0) return '0'
  if (shu >= 1000000) {
    return `${(shu / 1000000).toFixed(shu % 1000000 === 0 ? 0 : 1)}M`
  }
  if (shu >= 1000) {
    return `${(shu / 1000).toFixed(shu % 1000 === 0 ? 0 : 1)}K`
  }
  return shu.toString()
}

/**
 * Parse price sensitivity text to a level (1-4)
 * 1 = budget, 2 = moderate, 3 = splurge-worthy, 4 = indifferent
 */
export function parsePriceLevel(text: string): number {
  if (!text) return 2

  const lower = text.toLowerCase()

  if (lower.includes('budget') || lower.includes('value') || lower.includes('affordable')) {
    return 1
  }

  if (lower.includes('moderate') || lower.includes('balanced') || lower.includes('reasonable')) {
    return 2
  }

  if (lower.includes('splurge') || lower.includes('premium') || lower.includes('fine dining') || lower.includes('luxury')) {
    return 3
  }

  if (lower.includes('indifferent') || lower.includes('doesn\'t matter') || lower.includes('any price')) {
    return 4
  }

  return 2
}

/**
 * Get a short label for price level
 */
export function getPriceLevelLabel(level: number): string {
  const labels: Record<number, string> = {
    1: 'Budget-Friendly',
    2: 'Moderate',
    3: 'Splurge-Worthy',
    4: 'Price Indifferent',
  }
  return labels[level] || 'Moderate'
}

/**
 * Get the icon name for a portion preference value
 */
export function getPortionIcon(text: string): 'utensils' | 'users' | 'user' {
  if (!text) return 'utensils'

  const lower = text.toLowerCase()

  if (lower.includes('sharing') || lower.includes('share') || lower.includes('family')) {
    return 'users'
  }

  if (lower.includes('individual') || lower.includes('solo') || lower.includes('personal')) {
    return 'user'
  }

  // tasting-menu or default
  return 'utensils'
}

/**
 * Get a formatted label for portion preference
 */
export function getPortionLabel(text: string): string {
  if (!text) return 'Various'

  const lower = text.toLowerCase()

  if (lower.includes('tasting') || lower.includes('tasting-menu')) {
    return 'Tasting Menu'
  }

  if (lower.includes('sharing') || lower.includes('share')) {
    return 'Sharing Style'
  }

  if (lower.includes('individual') || lower.includes('solo')) {
    return 'Individual'
  }

  // Capitalize first letter of each word
  return text
    .split(/[-_\s]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}
